import { Controller } from "stimulus"

const DRAFT_STORAGE_PREFIX = "kyc-wizard-draft"
const DRAFT_SAVE_DELAY_MS = 400

export default class extends Controller {
  static targets = ["step", "stepIndicator", "progressSegment", "progressLabel", "prevButton", "nextButton", "submitActions", "shareholderRows", "shareholderRowTemplate", "validationSummary", "validationSummaryMessage", "draftStatus"]

  connect() {
    this.restoreDraft()
    this.currentStep = this.firstInvalidStepIndex()
    this.handleFieldUpdate = this.handleFieldUpdate.bind(this)
    this.handleUploaderUpdate = (event) => this.clearFieldContainer(event.target)
    this.clearDraft = this.clearDraft.bind(this)
    this.preventPrematureSubmit = this.preventPrematureSubmit.bind(this)
    this.preventSubmitDuringUpload = this.preventSubmitDuringUpload.bind(this)
    this.element.addEventListener("input", this.handleFieldUpdate)
    this.element.addEventListener("change", this.handleFieldUpdate)
    this.element.addEventListener("kycUpload:change", this.handleUploaderUpdate)
    this.element.addEventListener("keydown", this.preventPrematureSubmit)
    if (this.formElement) {
      this.formElement.addEventListener("submit", this.preventSubmitDuringUpload)
      this.formElement.addEventListener("submit", this.clearDraft)
    }
    this.render()
  }

  disconnect() {
    this.element.removeEventListener("input", this.handleFieldUpdate)
    this.element.removeEventListener("change", this.handleFieldUpdate)
    this.element.removeEventListener("kycUpload:change", this.handleUploaderUpdate)
    this.element.removeEventListener("keydown", this.preventPrematureSubmit)
    if (this.formElement) {
      this.formElement.removeEventListener("submit", this.preventSubmitDuringUpload)
      this.formElement.removeEventListener("submit", this.clearDraft)
    }
    window.clearTimeout(this.draftSaveTimeout)
  }

  // The final "Soumettre" button is the form's only type="submit" control, and it stays
  // in the DOM (just hidden) on every step but the last. That makes it the browser's
  // implicit submit target, so pressing Enter in a field on an earlier step would submit
  // the whole form prematurely instead of advancing the wizard. Route Enter through the
  // same validated step-advance as the "Suivant" button, except on the last step (where
  // Enter should submit) or inside a textarea (where Enter should insert a newline).
  preventPrematureSubmit(event) {
    if (event.key !== "Enter" || event.isComposing) return
    if (event.target && event.target.closest('textarea, button, a, [role="button"], select')) return
    if (this.currentStep >= this.stepTargets.length - 1) return

    event.preventDefault()
    this.next()
  }

  // The last step's documents are checked here: the final submit bypasses next().
  preventSubmitDuringUpload(event) {
    const lastSection = this.stepTargets[this.stepTargets.length - 1]
    const uploadIssues = this.uploaderEntries(lastSection)
    if (uploadIssues.length === 0) return

    event.preventDefault()
    event.stopImmediatePropagation()
    this.clearClientValidation(lastSection)
    uploadIssues.forEach((entry) => this.markInvalid(entry))
    this.showValidationSummary(uploadIssues.length)
    this.scrollToInvalidField(uploadIssues[0].field)
  }

  get formElement() {
    return this.element.querySelector("form")
  }

  get draftStorageKey() {
    return `${DRAFT_STORAGE_PREFIX}-${this.element.dataset.accountId}`
  }

  scheduleDraftSave() {
    window.clearTimeout(this.draftSaveTimeout)
    this.draftSaveTimeout = window.setTimeout(() => this.saveDraft(), DRAFT_SAVE_DELAY_MS)
  }

  saveDraft() {
    if (!this.formElement) return

    const entries = Array.from(new FormData(this.formElement).entries()).filter(([, value]) => !(value instanceof File))

    try {
      window.sessionStorage.setItem(this.draftStorageKey, JSON.stringify(entries))
    } catch (e) {
      // sessionStorage unavailable or full: draft persistence is a convenience, not a requirement.
    }
  }

  clearDraft() {
    try {
      window.sessionStorage.removeItem(this.draftStorageKey)
    } catch (e) {
      // ignore
    }
  }

  restoreDraft() {
    if (!this.formElement) return

    let raw
    try {
      raw = window.sessionStorage.getItem(this.draftStorageKey)
    } catch (e) {
      return
    }
    if (!raw) return

    let entries
    try {
      entries = JSON.parse(raw)
    } catch (e) {
      return
    }

    const savedValuesByName = {}
    entries.forEach(([name, value]) => {
      savedValuesByName[name] = savedValuesByName[name] || []
      savedValuesByName[name].push(value)
    })

    this.ensureShareholderRowsForDraft(savedValuesByName)

    const fieldsByName = {}
    this.formElement.querySelectorAll("input, select, textarea").forEach((field) => {
      if (field.type === "file" || !field.name) return
      fieldsByName[field.name] = fieldsByName[field.name] || []
      fieldsByName[field.name].push(field)
    })

    Object.keys(savedValuesByName).forEach((name) => {
      const fields = fieldsByName[name]
      if (!fields || fields.length === 0) return

      const isChoiceGroup = fields[0].type === "checkbox" || fields[0].type === "radio"

      if (isChoiceGroup) {
        // A DB-backed record already has one of these checked/selected: never override real data with a stale draft.
        if (fields.some((field) => field.checked)) return

        fields.forEach((field) => {
          field.checked = savedValuesByName[name].includes(field.value)
        })
      } else {
        fields.forEach((field, index) => {
          if (field.value.trim() !== "") return

          const value = savedValuesByName[name][index]
          if (value !== undefined) field.value = value
        })
      }
    })
  }

  ensureShareholderRowsForDraft(savedValuesByName) {
    if (!this.hasShareholderRowsTarget) return

    const savedCount = (savedValuesByName["account_legal_information[shareholders][][first_name]"] || []).length
    let currentCount = this.shareholderRowsTarget.querySelectorAll('[data-kycWizard-target="shareholderRow"]').length

    while (currentCount < savedCount) {
      this.addShareholderRow()
      currentCount += 1
    }
  }

  next() {
    window.clearTimeout(this.draftSaveTimeout)
    this.saveDraft()

    const currentSection = this.stepTargets[this.currentStep]
    this.clearClientValidation(currentSection)

    const invalidEntries = this.invalidEntries(currentSection)
    if (invalidEntries.length > 0) {
      invalidEntries.forEach((entry) => this.markInvalid(entry))
      this.showValidationSummary(invalidEntries.length)
      this.scrollToInvalidField(invalidEntries[0].field)
      return
    }

    this.hideValidationSummary()
    this.saveDraftToServer()

    if (this.currentStep < this.stepTargets.length - 1) {
      this.currentStep += 1
      this.render()
    }
  }

  invalidEntries(section) {
    const invalidEntries = []
    const processedRadioGroups = new Set()
    const fields = section.querySelectorAll("input, select, textarea")

    fields.forEach((field) => {
      if (field.disabled) return
      if (field.type === "checkbox" && (field.name || "").endsWith("[]")) return
      // Document uploaders are checked as a whole by uploaderEntries().
      if (field.closest('[data-controller~="kycUpload"]')) return

      if (field.type === "radio" && field.name) {
        if (processedRadioGroups.has(field.name)) return
        processedRadioGroups.add(field.name)
      }

      if (!field.checkValidity()) {
        invalidEntries.push({
          field,
          fields: this.groupFields(section, field),
          message: this.validationMessageFor(field)
        })
      }
    })

    const groups = {}
    section.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      if (!(checkbox.name || "").endsWith("[]")) return
      if (checkbox.dataset.optional === "true") return
      groups[checkbox.name] = groups[checkbox.name] || []
      groups[checkbox.name].push(checkbox)
    })

    for (const name in groups) {
      const boxes = groups[name]
      if (!boxes.some((checkbox) => checkbox.checked)) {
        invalidEntries.push({
          field: boxes[0],
          fields: boxes,
          message: "Veuillez sélectionner au moins une option."
        })
      }
    }

    const addShareholderButton = section.querySelector('[data-action~="click->kycWizard#addShareholderRow"]')
    if (addShareholderButton && this.declaresMajorShareholders() && this.shareholderCount() === 0) {
      invalidEntries.push({ field: addShareholderButton, fields: [addShareholderButton], message: "Ajoutez au moins un actionnaire." })
    }

    return invalidEntries.concat(this.uploaderEntries(section))
  }

  // Shown, enabled document uploaders of a step: upload still running, failed or refused
  // file, missing mandatory document, or fewer identity documents than shareholders.
  uploaderEntries(section) {
    const entries = []

    section.querySelectorAll('[data-controller~="kycUpload"]').forEach((element) => {
      const uploader = this.application.getControllerForElementAndIdentifier(element, "kycUpload")
      const browseInput = element.querySelector('input[type="file"]')
      if (!uploader || element.closest(".is-hidden") || (browseInput && browseInput.disabled)) return

      const entry = (message) => entries.push({ field: element, fields: [], message })

      if (uploader.isBusy()) {
        entry("Patientez : le fichier est en cours d'envoi.")
      } else if (uploader.hasFailedFiles()) {
        entry("Un fichier a été refusé (PDF ou image, 5 Mo maximum) ou n'a pas pu être envoyé : retirez-le ou réessayez.")
      } else if (browseInput && browseInput.required && uploader.fileCount() === 0) {
        entry("Veuillez ajouter un fichier (image ou PDF).")
      } else if ("kycShareholderDocuments" in element.dataset && this.declaresMajorShareholders() && uploader.fileCount() < this.shareholderCount()) {
        const expected = this.shareholderCount()
        entry(`Ajoutez une pièce d'identité par actionnaire : ${expected} attendue${expected > 1 ? "s" : ""}, ${uploader.fileCount()} jointe${uploader.fileCount() > 1 ? "s" : ""}.`)
      }
    })

    return entries
  }

  declaresMajorShareholders() {
    const yes = this.element.querySelector('input[type="radio"][name="account_legal_information[has_major_shareholders]"][value="true"]')
    return !!yes && yes.checked
  }

  shareholderCount() {
    return this.hasShareholderRowsTarget ? this.shareholderRowsTarget.querySelectorAll('[data-kycWizard-target="shareholderRow"]').length : 0
  }

  // Saves the progress on the server so a reload or another device finds it. Only rendered
  // with a draft URL for files still being filled in; the sessionStorage draft remains the fallback.
  saveDraftToServer() {
    const url = this.element.dataset.draftUrl
    if (!url || !this.formElement) return

    const body = new FormData(this.formElement)
    Array.from(body.entries()).forEach(([name, value]) => {
      if (value instanceof File) body.delete(name)
    })
    const token = document.querySelector('meta[name="csrf-token"]')

    window.fetch(url, {
      method: "PATCH",
      body,
      credentials: "same-origin",
      headers: { Accept: "application/json", "X-CSRF-Token": token ? token.content : "" }
    })
      .then((response) => this.showDraftStatus(response.ok || response.status === 409 ? null : "Vos réponses n'ont pas pu être enregistrées sur le serveur. Elles restent conservées dans cet onglet."))
      .catch(() => this.showDraftStatus("Connexion perdue : vos réponses restent conservées dans cet onglet, elles seront enregistrées à l'étape suivante."))
  }

  showDraftStatus(message) {
    if (!this.hasDraftStatusTarget) return

    this.draftStatusTarget.textContent = message || ""
    this.draftStatusTarget.classList.toggle("is-hidden", !message)
  }

  groupFields(section, field) {
    if (field.type !== "radio" || !field.name) return [field]

    return Array.from(section.querySelectorAll('input[type="radio"]')).filter((radio) => radio.name === field.name)
  }

  validationMessageFor(field) {
    const validity = field.validity
    if (!validity) return "Vérifiez la valeur saisie."

    if (validity.valueMissing) return this.missingValueMessageFor(field)

    if (validity.typeMismatch) {
      if (field.type === "url") return "Saisissez une adresse web complète, avec http:// ou https:// au début (ex. : https://www.monsite.com)."
      if (field.type === "email") return "Saisissez une adresse email valide (ex. : contact@monentreprise.com)."
    }

    if (field.type === "number") {
      if (validity.badInput) return "Saisissez un nombre valide (ex. : 25)."
      if (validity.rangeUnderflow) return `Saisissez un nombre supérieur ou égal à ${field.min}.`
      if (validity.rangeOverflow) return `Saisissez un nombre inférieur ou égal à ${field.max}.`
      if (validity.stepMismatch) return this.stepMismatchMessageFor(field)
    }

    if (validity.tooShort) return `Saisissez au moins ${field.minLength} caractères.`
    if (validity.tooLong) return `Saisissez au plus ${field.maxLength} caractères.`
    if (validity.patternMismatch) return "Le format saisi n'est pas valide."
    return "Vérifiez la valeur saisie."
  }

  // Derived from the field's own step attribute so the message never promises a rule the input doesn't enforce.
  stepMismatchMessageFor(field) {
    const decimals = (String(field.step).split(".")[1] || "").length
    if (decimals === 0) return "Saisissez un nombre entier."
    return `Saisissez un nombre avec au plus ${decimals} décimale${decimals > 1 ? "s" : ""}.`
  }

  missingValueMessageFor(field) {
    if (field.type === "file") return "Veuillez ajouter un fichier (image ou PDF)."
    if (field.type === "checkbox") return "Veuillez cocher cette case pour continuer."
    if (field.type === "radio") return "Veuillez sélectionner une option."
    if (field.tagName === "SELECT") return "Veuillez sélectionner une valeur dans la liste."
    return "Ce champ est obligatoire."
  }

  markInvalid({ field, fields, message }) {
    const container = field.closest(".field") || field.parentElement
    if (!container) return

    if (container.classList.contains("is-danger") && !container.classList.contains("kyc-field-invalid")) {
      container.dataset.kycServerError = "true"
    }

    container.classList.add("is-danger", "kyc-field-invalid")
    fields.forEach((invalidField) => invalidField.setAttribute("aria-invalid", "true"))

    if (!container.querySelector(".kyc-client-error")) {
      const error = document.createElement("p")
      error.className = "help is-danger kyc-client-error"
      error.textContent = message
      container.appendChild(error)
    }
  }

  showValidationSummary(count) {
    if (!this.hasValidationSummaryTarget) return

    this.validationSummaryTarget.classList.remove("is-hidden")
    if (this.hasValidationSummaryMessageTarget) {
      const label = count > 1 ? "champs obligatoires sont à compléter" : "champ obligatoire est à compléter"
      const location = count > 1 ? "Ils sont indiqués" : "Il est indiqué"
      this.validationSummaryMessageTarget.textContent = `${count} ${label}. ${location} en rouge ci-dessous.`
    }
  }

  hideValidationSummary() {
    if (this.hasValidationSummaryTarget) this.validationSummaryTarget.classList.add("is-hidden")
  }

  scrollToInvalidField(field) {
    const container = field.closest(".field") || field
    window.requestAnimationFrame(() => {
      container.scrollIntoView({ behavior: "smooth", block: "start" })
      window.setTimeout(() => this.focusField(field), 350)
    })
  }

  focusField(field) {
    const choicesInput = field.closest(".control") && field.closest(".control").querySelector(".choices input")
    const flatpickrInput = field._flatpickr && field._flatpickr.altInput
    const focusTarget = choicesInput || flatpickrInput || field

    if (focusTarget && typeof focusTarget.focus === "function") focusTarget.focus({ preventScroll: true })
  }

  handleFieldUpdate(event) {
    const field = event.target
    if (!field || !field.closest) return

    if (field.type !== "file") this.scheduleDraftSave()

    if (field.type === "checkbox" && (field.name || "").endsWith("[]")) {
      const section = field.closest('[data-kycWizard-target="step"]')
      if (!section) return
      const group = Array.from(section.querySelectorAll('input[type="checkbox"]')).filter((checkbox) => checkbox.name === field.name)
      if (group.some((checkbox) => checkbox.checked)) this.clearFieldContainer(field)
      return
    }

    if (field.checkValidity && field.checkValidity()) {
      this.clearFieldContainer(field)
    }
  }

  clearFieldContainer(field) {
    const container = field.closest(".field")
    if (!container || !container.classList.contains("kyc-field-invalid")) return

    container.classList.remove("kyc-field-invalid")
    if (container.dataset.kycServerError !== "true") container.classList.remove("is-danger")
    container.querySelectorAll('[aria-invalid="true"]').forEach((input) => input.removeAttribute("aria-invalid"))
    const error = container.querySelector(".kyc-client-error")
    if (error) error.remove()
  }

  clearClientValidation(section) {
    section.querySelectorAll(".kyc-field-invalid").forEach((container) => {
      container.classList.remove("kyc-field-invalid")
      if (container.dataset.kycServerError !== "true") container.classList.remove("is-danger")
      container.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute("aria-invalid"))
      const error = container.querySelector(".kyc-client-error")
      if (error) error.remove()
    })
  }

  previous() {
    if (this.currentStep > 0) {
      this.hideValidationSummary()
      this.currentStep -= 1
      this.render()
    }
  }

  render() {
    this.stepTargets.forEach((section, index) => {
      section.classList.toggle("is-hidden", index !== this.currentStep)
    })

    if (this.hasStepIndicatorTarget) {
      this.stepIndicatorTarget.textContent = `Étape ${this.currentStep + 1} / ${this.stepTargets.length}`
    }

    this.progressSegmentTargets.forEach((segment, index) => {
      segment.style.width = index <= this.currentStep ? "100%" : "0%"
    })

    this.progressLabelTargets.forEach((label, index) => {
      label.classList.toggle("has-text-weight-bold", index === this.currentStep)
      label.style.color = index === this.currentStep ? "#1B2A5B" : "#7A8299"
    })

    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.classList.toggle("is-hidden", this.currentStep === 0)
    }

    const isLastStep = this.currentStep === this.stepTargets.length - 1
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle("is-hidden", isLastStep)
    }
    if (this.hasSubmitActionsTarget) {
      this.submitActionsTarget.classList.toggle("is-hidden", !isLastStep)
    }
  }

  firstInvalidStepIndex() {
    for (let index = 0; index < this.stepTargets.length; index += 1) {
      if (this.stepTargets[index].querySelector(".is-danger")) {
        return index
      }
    }
    return 0
  }

  addShareholderRow() {
    const template = this.shareholderRowTemplateTarget.content.cloneNode(true)
    this.shareholderRowsTarget.appendChild(template)
  }

  removeShareholderRow(event) {
    event.target.closest('[data-kycWizard-target="shareholderRow"]').remove()
  }
}
